/**
 * 文件处理工具类
 */
import request from "../api/request.js";
import { createLogger } from "./logger.js";

const logger = createLogger("File");

/**
 * 上传状态码定义
 */
export const UPLOAD_STATUS = {
  PROCESSING: -1, // 处理中
  WAITING: 0, // 等待上传
  PENDING: 6, // 待上传（已点击上传，等待开始）
  UPLOADING: 1, // 上传中
  PAUSED: 5, // 已暂停
  SUCCESS: 2, // 上传成功
  ERROR: 3, // 上传失败
  CANCELLED: 4, // 已取消
};

/**
 * 格式化文件大小
 * @param {number} bytes - 文件大小（字节）
 * @returns {string} 格式化后的文件大小字符串
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * 格式化剩余时间（时分秒）
 * 少于 1 小时不显示小时，少于 1 分钟不显示分钟
 * @param {number} totalSeconds - 总秒数
 * @returns {string} 如 "45秒" / "3分12秒" / "1时5分30秒"
 */
export const formatRemainingTime = (totalSeconds) => {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return "";
  const sec = Math.floor(totalSeconds);
  if (sec < 60) {
    if (sec === 0 && totalSeconds > 0) return "1秒内";
    return `${sec}秒`;
  }
  const minutes = Math.floor(sec / 60);
  const seconds = sec % 60;
  if (sec < 3600) {
    return seconds > 0 ? `${minutes}分${seconds}秒` : `${minutes}分`;
  }
  const hours = Math.floor(sec / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  const secs = sec % 60;
  const parts = [`${hours}时`];
  if (mins > 0) parts.push(`${mins}分`);
  if (secs > 0) parts.push(`${secs}秒`);
  return parts.join("");
};

/**
 * 解析文件大小字符串为字节数
 * @param {string} sizeStr - 文件大小字符串，如 "5.00 GB"
 * @returns {number} 字节数
 */
export const parseFileSize = (sizeStr) => {
  const units = {
    B: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
    TB: 1024 * 1024 * 1024 * 1024,
  };
  const match = sizeStr.match(/^(\d+(?:\.\d+)?)\s*(B|KB|MB|GB|TB)$/i);
  if (match) {
    const size = parseFloat(match[1]);
    const unit = match[2].toUpperCase();
    return size * units[unit];
  }
  return 0;
};

/**
 * 生成不重复的文件名：当存在同名时，在名称后追加 (n)
 * @param {string} originalName - 原始文件名
 * @param {Set<string>} takenNames - 已占用的文件名集合
 * @returns {string} 不重复的文件名
 */
export const generateUniqueFileName = (originalName, takenNames) => {
  const dot = originalName.lastIndexOf(".");
  const hasExt = dot > 0;
  const base = hasExt ? originalName.slice(0, dot) : originalName;
  const ext = hasExt ? originalName.slice(dot) : "";

  // 去掉末尾已存在的 (n) 以便连续编号
  const match = base.match(/^(.*) \((\d+)\)$/);
  const cleanBase = match ? match[1] : base;

  let n = 1;
  let candidate = `${cleanBase}(${n})${ext}`;
  while (takenNames.has(candidate)) {
    n += 1;
    candidate = `${cleanBase}(${n})${ext}`;
  }
  return candidate;
};

/**
 * 读取 Blob/File 为 ArrayBuffer
 * - 优先使用 blob.arrayBuffer()（在 Worker 中也可用）
 * - 兼容降级到 FileReader（若存在）
 * @param {Blob} blob
 * @returns {Promise<ArrayBuffer>}
 */
const readBlobAsArrayBuffer = async (blob) => {
  if (blob && typeof blob.arrayBuffer === "function") {
    return await blob.arrayBuffer();
  }
  if (typeof FileReader !== "undefined") {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }
  throw new Error("当前环境不支持读取文件内容（FileReader/arrayBuffer 不可用）");
};

/**
 * 计算文件 SHA1 哈希值
 * @param {File} file - 文件对象
 * @returns {Promise<string>} SHA1 哈希值
 */
export const calculateFileHash = async (file) => {
  const arrayBuffer = await readBlobAsArrayBuffer(file);
  const hashBuffer = await crypto.subtle.digest("SHA-1", arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex.toUpperCase();
};

/**
 * 快速检查文件名和大小（不计算哈希，用于 handleFileAdd）
 * @param {Array<File>} newFiles - 新文件列表
 * @param {Array} existingFiles - 已存在的文件列表（包含 {name, size} 等属性）
 * @param {Object} options - 配置选项
 * @param {boolean} options.allowMultiple - 是否允许多选
 * @param {number|string} options.maxFileSizeBytes - 单个文件最大大小（字节）或字符串格式（如 "10GB"）
 * @param {number|string} options.maxTotalSizeBytes - 总大小限制（字节）或字符串格式（如 "5GB"）
 * @param {number} options.currentTotalSize - 当前已使用总大小（字节），如果不传则从 existingFiles 自动计算
 * @param {Function} options.onError - 错误回调函数 (title, message, type) => void
 * @returns {Array} 处理后的文件项列表，格式：[{name, size, type, file}, ...]，包含重命名后的文件名
 */
export const checkFileNamesAndSizes = (
  newFiles,
  existingFiles = [],
  options = {}
) => {
  const {
    allowMultiple = true,
    maxFileSizeBytes: maxFileSizeInput,
    maxTotalSizeBytes: maxTotalSizeInput,
    currentTotalSize: currentTotalSizeInput,
    onError = () => {},
  } = options;

  // 自动计算或解析 maxFileSizeBytes
  let maxFileSizeBytes = Infinity;
  if (maxFileSizeInput !== undefined && maxFileSizeInput !== null) {
    if (typeof maxFileSizeInput === "string") {
      maxFileSizeBytes = parseFileSize(maxFileSizeInput);
    } else {
      maxFileSizeBytes = maxFileSizeInput;
    }
  }

  // 自动计算或解析 maxTotalSizeBytes
  let maxTotalSizeBytes = Infinity;
  if (maxTotalSizeInput !== undefined && maxTotalSizeInput !== null) {
    if (typeof maxTotalSizeInput === "string") {
      maxTotalSizeBytes = parseFileSize(maxTotalSizeInput);
    } else {
      maxTotalSizeBytes = maxTotalSizeInput;
    }
  }

  // 自动计算 currentTotalSize（如果不传则从 existingFiles 计算）
  let currentTotalSize = 0;
  if (currentTotalSizeInput !== undefined && currentTotalSizeInput !== null) {
    currentTotalSize = currentTotalSizeInput;
  } else {
    // 从 existingFiles 自动计算
    currentTotalSize = existingFiles.reduce(
      (total, file) => total + (file.size || 0),
      0
    );
  }

  logger.info("newFiles:", newFiles);
  logger.info("existingFiles:", existingFiles);
  logger.info("allowMultiple:", allowMultiple);
  logger.info("maxFileSizeBytes:", maxFileSizeBytes);
  logger.info("maxTotalSizeBytes:", maxTotalSizeBytes);
  logger.info("currentTotalSize:", currentTotalSize);

  // 检查是否允许多选
  if (!allowMultiple && newFiles.length > 1) {
    onError("选择文件限制", "当前设置不允许选择多个文件", "warning");
    return [];
  }

  if (existingFiles.length + newFiles.length > 100) {
    onError("文件数超出限制", "当前设置不允许选择超过100个文件", "error");
    return [];
  }

  // 计算新文件的总大小
  const newFilesTotalSize = newFiles.reduce(
    (total, file) => total + file.size,
    0
  );

  // 检查总大小是否超出限制
  if (currentTotalSize + newFilesTotalSize > maxTotalSizeBytes) {
    const currentSizeStr = formatFileSize(currentTotalSize);
    const newSizeStr = formatFileSize(newFilesTotalSize);
    onError(
      "空间不足",
      `添加这些文件后总大小将超过限制！\n\n当前已使用：${currentSizeStr}\n新增文件：${newSizeStr}`,
      "error"
    );
    return [];
  }

  const processedFiles = [];

  for (const file of newFiles) {
    // 检查单个文件大小限制
    if (file.size > maxFileSizeBytes) {
      onError(
        "文件过大",
        `文件 "${file.name}" 超过最大文件大小限制 ${formatFileSize(
          maxFileSizeBytes
        )}`,
        "error"
      );
      return [];
    }

    processedFiles.push({
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
    });
  }

  return processedFiles;
};

/**
 * 检查文件内容对比（使用文件名和大小对比，用于 handleAllProcessed）
 * @param {Object} fileItem - 文件项（包含 {name, size, file} 等属性）
 * @param {Array} existingFiles - 已存在的文件列表（包含 {name, size, file} 等属性）
 * @param {Object} options - 配置选项
 * @param {Function} options.onWarning - 警告回调函数 (title, message) => void
 * @param {Function} options.getLogger - 获取logger的函数，返回 logger 对象
 * @param {Set} options.takenNames - 已占用的文件名集合（用于重命名，可选）
 * @returns {Promise<Object|null>} 处理后的文件项，去重和重命名后的结果，如果重复则返回 null
 */
export const checkFileContent = async (
  fileItem,
  existingFiles = [],
  options = {}
) => {
  const {
    onWarning = () => {},
    getLogger = () => ({ debug: () => {}, error: () => {}, info: () => {} }),
    takenNames: takenNamesInput,
  } = options;

  const logger = getLogger();
  
  // 记录已占用的文件名（用于重命名）
  // 如果传入了 takenNames，则使用它；否则从 existingFiles 初始化
  const takenNames = takenNamesInput || new Set(existingFiles.map((f) => f.name));

  const file = fileItem.file;

  // 使用文件名和大小对比，查找重复文件（与 existingFiles 对比）
  let hasDuplicate = false;
  let duplicateExistingName = fileItem.name;
  for (const existing of existingFiles) {
    // 对比文件名和大小
    if (existing.name === fileItem.name && existing.size === file.size) {
      hasDuplicate = true;
      duplicateExistingName = existing.name;
      break;
    }
  }

  if (hasDuplicate) {
    onWarning(
      "重复文件",
      `已存在同名同大小的文件："${duplicateExistingName}"，已跳过添加。`
    );
    return null;
  }

  // 检查文件名是否重复（包括 existingFiles 和已处理的文件），自动重命名
  let finalName = fileItem.name;
  if (takenNames.has(finalName)) {
    finalName = generateUniqueFileName(fileItem.name, takenNames);
  }
  logger.info("finalName:", finalName);
  // 将重命名后的文件名添加到 takenNames，确保后续文件中的同名文件也会被重命名
  takenNames.add(finalName);

  // 返回重命名后的文件项
  return {
    ...fileItem,
    name: finalName,
  };
};

/**
 * 处理文件列表：验证、去重、重命名（保留原有接口，内部调用新方法）
 * @param {Array<File>} newFiles - 新文件列表
 * @param {Array} existingFiles - 已存在的文件列表（包含 {name, size, file} 等属性）
 * @param {Object} options - 配置选项
 * @param {boolean} options.allowMultiple - 是否允许多选
 * @param {number} options.maxFileSizeBytes - 单个文件最大大小（字节）
 * @param {number} options.maxTotalSizeBytes - 总大小限制（字节）
 * @param {number} options.currentTotalSize - 当前已使用总大小（字节）
 * @param {Function} options.onError - 错误回调函数 (title, message, type) => void
 * @param {Function} options.onWarning - 警告回调函数 (title, message) => void
 * @param {Function} options.getLogger - 获取logger的函数，返回 logger 对象
 * @returns {Promise<Array>} 处理后的文件项列表，格式：[{id, name, size, type, file, ...}, ...]
 */
export const processFiles = async (
  newFiles,
  existingFiles = [],
  options = {}
) => {
  // 先检查文件名和大小
  const nameCheckedFiles = checkFileNamesAndSizes(
    newFiles,
    existingFiles,
    options
  );
  if (nameCheckedFiles.length === 0) {
    return [];
  }

  // 再检查文件内容（循环处理每个文件）
  const processedFiles = [];
  const takenNames = new Set(existingFiles.map((f) => f.name));
  
  for (const fileItem of nameCheckedFiles) {
    const result = await checkFileContent(
      fileItem,
      existingFiles,
      {
        ...options,
        takenNames: takenNames
      }
    );
    if (result) {
      processedFiles.push(result);
    }
  }

  return processedFiles;
};

/**
 * 计算文件前 1KB 的 SHA1（pre_hash），用于预秒传
 * @param {File} file - 文件对象
 * @returns {Promise<string>} SHA1 哈希值
 */
export const calculatePreHash = async (file) => {
  const size = Math.min(1024, file.size);
  const blob = file.slice(0, size);
  const arrayBuffer = await readBlobAsArrayBuffer(blob);
  const hashBuffer = await crypto.subtle.digest("SHA-1", arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex.toUpperCase();
};

/**
 * 生成分片信息列表
 * @param {number} fileSize - 文件大小
 * @param {number} chunkSize - 分片大小，默认 10MB
 * @returns {Array} 分片信息列表
 */
export const generatePartInfoList = (
  fileSize,
  chunkSize = 10 * 1024 * 1024
) => {

  // 动态调整分片大小，避免分片数量过多
  // 如果文件大于 50GB，使用 50MB 分片；大于 100GB，使用 100MB 分片
  let finalChunkSize = chunkSize;
  if (fileSize > 200 * 1024 * 1024 * 1024) { // 200GB
    finalChunkSize = 400 * 1024 * 1024; // 400MB
  } else if (fileSize > 50 * 1024 * 1024 * 1024) { // 50GB
    finalChunkSize = 100 * 1024 * 1024; // 100MB
  } else if (fileSize > 10 * 1024 * 1024 * 1024) { // 10GB
    finalChunkSize = 50 * 1024 * 1024; // 50MB
  } else if (fileSize > 5 * 1024 * 1024 * 1024) { // 5GB
    finalChunkSize = 20 * 1024 * 1024; // 20MB
  }

  const partInfoList = [];
  let partNumber = 1;
  let from = 0;

  while (from < fileSize) {
    const to = Math.min(from + finalChunkSize - 1, fileSize - 1);
    const partSize = to - from + 1;

    partInfoList.push({
      partNumber: partNumber,
      partSize: partSize,
      // from: from,
      // to: to,
    });

    partNumber++;
    from = to + 1;
  }

  return partInfoList;
};

/**
 * 获取文件 MIME 类型
 * @param {File} file - 文件对象
 * @returns {string} MIME 类型
 */
export const getFileMimeType = (file) => {
  return file.type || "application/octet-stream";
};

/**
 * 读取文件切片为 Uint8Array
 * @param {File} file - 文件对象
 * @param {number} start - 起始位置
 * @param {number} length - 读取长度
 * @returns {Promise<Uint8Array>} 文件切片数据
 */
export const readFileSlice = (file, start, length) => {
  const blob = file.slice(start, start + length);
  return readBlobAsArrayBuffer(blob).then((buf) => new Uint8Array(buf));
};

/**
 * 按照指定算法生成 proof_code
 * 1) 从服务器获取 prefix（16位 hex）；
 * 2) 转为 uint64；
 * 3) r = uint64 % size
 * 4) 读取文件 [r, r+8)（若超出文件尾则至 size）
 * 5) base64 该 0..8 字节
 * @param {File} file - 文件对象
 * @returns {Promise<string>} proof_code
 */
export const generateProofCodeFromAccessToken = async (file) => {
  try {
    if (!file) return "";
    const size = file.size >>> 0;
    if (!size) return "";
    // 从服务器获取 prefix
    const prefixResult = await request.get("/api/file/getPrefix");
    let prefix16 = "";
    if (prefixResult.success) {
      prefix16 = prefixResult.data;
    } else {
      return "";
    }
    prefix16 = prefix16.slice(0, 16);
    const u64 = BigInt("0x" + prefix16);
    const r = Number(u64 % BigInt(size));
    const end = Math.min(r + 8, size);
    const slice = await readFileSlice(file, r, end - r);
    let binary = "";
    for (let i = 0; i < slice.length; i++)
      binary += String.fromCharCode(slice[i]);
    return btoa(binary);
  } catch (e) {
    logger.error("generateProofCodeFromAccessToken 失败:", e);
    return "";
  }
};

/**
 * 更新上传进度
 * @param {number} progress - 进度百分比（0-100）
 * @param {Object} fileItem - 文件项对象（包含 progress, size, uploadedSize 等属性）
 * @param {Function} onUpdateSpeed - 速度更新回调函数（可选）
 * @param {boolean} forceUpdate - 是否强制更新（即使变化小于0.1%）
 * @returns {boolean} 是否实际更新了进度
 */
export const updateUploadProgress = (
  progress,
  fileItem,
  onUpdateSpeed,
  forceUpdate = false
) => {
  // 按0.1%的间隔更新进度
  const roundedProgress =
    Math.round(Math.max(0, Math.min(100, progress)) * 10) / 10;

  // 只有进度变化超过0.1%时才更新，或者强制更新
  const shouldUpdate =
    forceUpdate || Math.abs((fileItem.progress || 0) - roundedProgress) >= 0.1;

  if (shouldUpdate) {
    fileItem.progress = roundedProgress;
    // 实时更新已上传大小
    fileItem.uploadedSize = (fileItem.progress / 100) * fileItem.size;

    // 触发速度更新（如果提供了回调）
    if (onUpdateSpeed) {
      onUpdateSpeed(fileItem);
    }

    return true;
  }

  return false;
};

// 上传速度计算辅助变量
const uploadSpeedTrackers = new Map();
const animationFrameIds = new Map();

// 单次采样速度上限（约 150 MB/s 原始值），仅在没有分片耗时时作为 fallback 限幅
const MAX_SPEED_SAMPLE_BYTES_PER_SEC = 150 * 1024 * 1024;

/**
 * 上报单个分片上传完成，用累计分片数据计算速度与剩余时间（避免最后一块小导致剩余时间反升）
 * @param {Object} fileItem - 文件项（含 id、uploadSpeed 等）
 * @param {number} chunkSizeBytes - 该分片字节数
 * @param {number} durationMs - 该分片实际上传耗时（毫秒）
 */
export const reportChunkUploadDone = (fileItem, chunkSizeBytes, durationMs) => {
  if (!fileItem || chunkSizeBytes <= 0 || durationMs <= 0) return;
  let tracker = uploadSpeedTrackers.get(fileItem.id);
  if (!tracker) {
    tracker = {
      lastTime: Date.now(),
      lastUploadedSize: 0,
      speeds: [],
      totalChunkBytes: 0,
      totalChunkTimeMs: 0,
    };
    uploadSpeedTrackers.set(fileItem.id, tracker);
  }
  tracker.totalChunkBytes = (tracker.totalChunkBytes || 0) + chunkSizeBytes;
  tracker.totalChunkTimeMs = (tracker.totalChunkTimeMs || 0) + durationMs;
  // 用累计字节/累计耗时作为速度，剩余时间 = 剩余字节/该速度，网络平稳时单调下降
  const overallSpeedBytesPerSec =
    tracker.totalChunkTimeMs > 0
      ? (tracker.totalChunkBytes * 1000) / tracker.totalChunkTimeMs
      : 0;
  fileItem.uploadSpeed = overallSpeedBytesPerSec;
};

/**
 * 实时更新上传速度和已上传大小（使用 requestAnimationFrame）
 * @param {Object} fileItem - 文件项对象（包含 id, uploadStatus, progress, size, uploadedSize, uploadSpeed 等属性）
 */
export const startSpeedUpdate = (fileItem) => {
  if (animationFrameIds.has(fileItem.id)) return;

  let tracker = uploadSpeedTrackers.get(fileItem.id);
  if (tracker && tracker.totalChunkTimeMs > 0) {
    fileItem.uploadSpeed = (tracker.totalChunkBytes * 1000) / tracker.totalChunkTimeMs;
  }
  if (!tracker) {
    // 续传时以当前进度为基线，避免把「已上传量」误算成「刚传的」导致速度暴增
    const initialUploadedSize =
      fileItem.size > 0
        ? (Math.max(0, Math.min(100, fileItem.progress || 0)) / 100) * fileItem.size
        : 0;
    uploadSpeedTrackers.set(fileItem.id, {
      lastTime: Date.now(),
      lastProgress: fileItem.progress ?? 0,
      lastUploadedSize: initialUploadedSize,
      speeds: [],
      totalChunkBytes: 0,
      totalChunkTimeMs: 0,
    });
    // 有已有进度时（如续传）不重置显示，避免闪一下 0
    if (initialUploadedSize <= 0) {
      fileItem.uploadedSize = 0;
      fileItem.uploadSpeed = 0;
    }
  }

  const updateFrame = () => {
    // uploadStatus === 1 表示上传中
    if (
      fileItem.uploadStatus !== UPLOAD_STATUS.UPLOADING ||
      !animationFrameIds.has(fileItem.id)
    ) {
      animationFrameIds.delete(fileItem.id);
      return;
    }

    const now = Date.now();
    const tracker = uploadSpeedTrackers.get(fileItem.id);

    // 每一帧都更新已上传大小（基于进度），确保实时刷新
    const currentUploadedSize = fileItem.size > 0 ? (fileItem.progress / 100) * fileItem.size : 0;
    const roundedUploadedSize =
      fileItem.size > 0
        ? (Math.round((currentUploadedSize / fileItem.size) * 1000) / 1000) * fileItem.size
        : 0;

    // 强制更新响应式数据，确保视图更新
    if (Math.abs(fileItem.uploadedSize - roundedUploadedSize) > 0) {
      fileItem.uploadedSize = roundedUploadedSize;
    }

    if (tracker) {
      // 已有分片累计数据时，速度由 reportChunkUploadDone 按累计字节/耗时更新，此处不再用帧间隔推算
      const useChunkSpeed = tracker.totalChunkTimeMs > 0;
      if (!useChunkSpeed) {
        const timeDiff = (now - tracker.lastTime) / 1000; // 秒
        if (timeDiff >= 0.1) {
          const sizeDiff = currentUploadedSize - tracker.lastUploadedSize;
          let speed = sizeDiff / timeDiff;
          speed = Math.min(speed, MAX_SPEED_SAMPLE_BYTES_PER_SEC);
          if (tracker.speeds.length > 0) {
            const currentAvg =
              tracker.speeds.reduce((a, b) => a + b, 0) / tracker.speeds.length;
            if (currentAvg > 0 && speed > currentAvg * 2) {
              speed = Math.min(speed, currentAvg * 1.5);
            }
          }
          if (speed > 0 || tracker.speeds.length === 0) {
            tracker.speeds.push(speed);
            if (tracker.speeds.length > 10) tracker.speeds.shift();
          }
          const avgSpeed =
            tracker.speeds.length > 0
              ? tracker.speeds.reduce((a, b) => a + b, 0) / tracker.speeds.length
              : 0;
          fileItem.uploadSpeed = avgSpeed > 0 ? avgSpeed / 10 : 0;
          tracker.lastTime = now;
          tracker.lastUploadedSize = currentUploadedSize;
        }
      }
    }

    // 继续下一帧
    const frameId = requestAnimationFrame(updateFrame);
    animationFrameIds.set(fileItem.id, frameId);
  };

  const frameId = requestAnimationFrame(updateFrame);
  animationFrameIds.set(fileItem.id, frameId);
};

/**
 * 停止速度更新
 * @param {string|number} fileItemId - 文件项ID
 * @param {Object} [options] - 选项
 * @param {boolean} [options.deleteTracker=true] - 是否删除 tracker；暂停时传 false 以保留累计数据，续传后速度连续
 */
export const stopSpeedUpdate = (fileItemId, options = {}) => {
  if (animationFrameIds.has(fileItemId)) {
    cancelAnimationFrame(animationFrameIds.get(fileItemId));
    animationFrameIds.delete(fileItemId);
  }
  const deleteTracker = options.deleteTracker !== false;
  if (deleteTracker) {
    uploadSpeedTrackers.delete(fileItemId);
  }
};

/**
 * 计算和更新上传速度（兼容旧代码，但主要使用 startSpeedUpdate）
 * @param {Object} fileItem - 文件项对象（包含 id, uploadStatus, progress 等属性）
 */
export const updateUploadSpeed = (fileItem) => {
  // 这个方法现在主要用于触发实时更新
  // uploadStatus === 1 表示上传中
  if (fileItem.uploadStatus !== UPLOAD_STATUS.UPLOADING || !fileItem.progress) {
    stopSpeedUpdate(fileItem.id);
    return;
  }

  // 确保实时更新已启动
  if (!animationFrameIds.has(fileItem.id)) {
    startSpeedUpdate(fileItem);
  }
};

/**
 * 触发文件下载（通过Blob）
 * @param {string} url - 文件URL
 * @param {string} fileName - 文件名
 */
export const triggerFileDownload = (url, fileName) => {
  // 创建下载链接
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = url;
  link.download = fileName;

  // 添加到DOM并触发点击
  document.body.appendChild(link);
  link.click();

  // 清理
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * 返回签名后下载地址
 * @param {string} url - 下载地址
 * @returns {string} 返回后的下载地址
 */
export const backToDownloadUrl = (url) => {
  const basePath =
    // 编译时注入：优先使用 .env 配置的站点地址（需以 VITE_ 开头）
    (import.meta.env?.VITE_FILE_SITE_ORIGIN || "").trim() ||
    // 运行时：使用当前访问域名
    (typeof window !== "undefined" && window.location?.origin ? window.location.origin : "");
  if (basePath === "") {
    return url;
  }
  let downloadUrl = url;
  if (downloadUrl.startsWith("https://cn-beijing-data.aliyundrive.net")) {
    downloadUrl = downloadUrl.replace(
      "https://cn-beijing-data.aliyundrive.net",
      `${basePath}/api/download`
    );
  } else if (downloadUrl.startsWith("http://cn-beijing-data.aliyundrive.net")) {
    downloadUrl = downloadUrl.replace(
      "http://cn-beijing-data.aliyundrive.net",
      `${basePath}/api/download`
    );
  }
  return downloadUrl;
}
